import React, { useMemo } from 'react';
import type { Test } from '@mariana/labos-client';
import { WorksheetViewer } from '../WorksheetViewer';
import { SpreadsheetTable } from '../SpreadsheetTable';
import { formatAnalyticalValue } from '../../utils/formatValue';
import './ResultViewers.css';

interface NMRResultViewerProps {
  test: Test;
}

const NMR_KEYS: { key: string; element: string }[] = [
  { key: 'B_result', element: 'B' },
  { key: 'Br_result', element: 'Br' },
  { key: 'Cl', element: 'Cl' },
  { key: 'Li_result', element: 'Li' },
  { key: 'Na_result', element: 'Na' },
];

export const NMRResultViewer: React.FC<NMRResultViewerProps> = ({ test }) => {
  const worksheetData = test.attributes?.worksheet_data as Record<string, any> | undefined;
  const rawWorksheetData = test.attributes?.raw_worksheet_data as Record<string, Record<string, any>> | undefined;

  const reportedRows = useMemo(() => {
    if (!worksheetData) return [];
    return NMR_KEYS.map(({ key, element }) => {
      const entry = worksheetData[key];
      const value = entry?.value ?? entry;
      return [element, formatAnalyticalValue(value)] as [string, string];
    });
  }, [worksheetData]);

  return (
    <div className="rv-container">
      {rawWorksheetData && (
        <div className="rv-section rv-section-raw">
          <h3 className="rv-section-title">Raw Worksheet</h3>
          <WorksheetViewer rawWorksheetData={rawWorksheetData} />
        </div>
      )}

      {worksheetData && reportedRows.length > 0 && (
        <div className="rv-section rv-section-reported">
          <h3 className="rv-section-title">Reported Values</h3>
          <SpreadsheetTable
            headers={['Element', 'Value']}
            rows={reportedRows}
            showRowNumbers={false}
          />
        </div>
      )}

      {!worksheetData && !rawWorksheetData && (
        <div className="rv-empty">No NMR results available for this test.</div>
      )}
    </div>
  );
};
